/**
 * Ön sipariş consent gate for the PDP buy box.
 *
 * The `required` attribute on the consent checkbox is what actually stops the
 * sale — it makes `form.checkValidity()` false, which the theme's
 * AddToCartComponent already honours. This module exists for the two places
 * where that is not enough:
 *
 *  1. The browser's own "please tick this box" balloon is suppressed here and
 *     replaced with an in-page message. Left to the browser it is a transient
 *     tooltip that no screen reader path and no mobile Safari user reliably
 *     gets, and the shopper is left with a button that looks broken.
 *  2. The sticky bar (assets/sticky-add-to-cart.js) forwards a synthetic click
 *     to the real button and then plays its "eklendi" animation regardless of
 *     what that click did. Without this guard the shopper is told the item is
 *     in the cart when the form refused it.
 *
 * When the panel renders `data-xtp-ready="false"` — no delivery date, or no
 * published consent text — there is no checkbox to tick and every add-to-cart
 * path is refused outright.
 *
 * It also takes a confirmed preorder straight to checkout rather than into the
 * cart. A preorder is a commitment to a delivery window the buyer has just
 * agreed to; parking it in the cart invites a second, unrelated item to join
 * it, and a mixed order ships as one consignment, which drags the in-stock
 * item out to the preorder's date without anyone having said so.
 */

const NOTICE = '[data-xtp-notice]';
const CONSENT = '[data-xtp-consent]';
const ERROR = '[data-xtp-error]';
const ADD_BUTTON = '.add-to-cart-button, [ref="addToCartButton"]';

/**
 * The panel governing the main product form. Quick-add and other dialogs carry
 * their own product form, so they are skipped rather than borrowed from.
 * @returns {HTMLElement | null}
 */
function mainNotice() {
  for (const el of document.querySelectorAll(NOTICE)) {
    if (!el.closest('dialog')) return /** @type {HTMLElement} */ (el);
  }
  return null;
}

/**
 * @param {Element} button
 * @returns {HTMLElement | null}
 */
function noticeFor(button) {
  const form = button.closest('product-form-component');
  if (form) return form.querySelector(NOTICE);
  // The sticky bar sits outside the form it drives, so it answers to the panel
  // of the main product.
  return button.closest('sticky-add-to-cart') ? mainNotice() : null;
}

/** @param {HTMLElement} notice */
function showError(notice) {
  notice.classList.add('xtp--invalid');

  const error = notice.querySelector(ERROR);
  if (error instanceof HTMLElement) error.hidden = false;

  notice.scrollIntoView({ block: 'center', behavior: 'smooth' });

  const box = notice.querySelector(CONSENT);
  if (box instanceof HTMLInputElement) box.focus({ preventScroll: true });
}

/** @param {HTMLElement} notice */
function clearError(notice) {
  notice.classList.remove('xtp--invalid');

  const error = notice.querySelector(ERROR);
  if (error instanceof HTMLElement) error.hidden = true;
}

/**
 * @param {HTMLElement} notice
 * @returns {boolean} Whether a preorder may proceed right now.
 */
function consented(notice) {
  if (notice.dataset.xtpReady !== 'true') return false;

  const box = notice.querySelector(CONSENT);
  return box instanceof HTMLInputElement ? box.checked : false;
}

// Capture phase on the document: the click has to die before it reaches the
// button, otherwise the theme's own handlers have already started animating.
document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest(ADD_BUTTON);
    if (!button) return;

    const notice = noticeFor(button);
    if (!notice || consented(notice)) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    showError(notice);
  },
  true
);

// `invalid` does not bubble, hence the capture listener on the document.
document.addEventListener(
  'invalid',
  (event) => {
    const box = event.target;
    if (!(box instanceof HTMLInputElement) || !box.matches(CONSENT)) return;

    event.preventDefault();

    const notice = box.closest(NOTICE);
    if (notice instanceof HTMLElement) showError(notice);
  },
  true
);

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement) || !target.matches(CONSENT)) return;
  if (!target.checked) return;

  const notice = target.closest(NOTICE);
  if (notice instanceof HTMLElement) clearError(notice);
});

/**
 * Keeps the sticky bar's button disabled while no preorder can be taken.
 * sections/product-information.liquid derives its disabled state from
 * `variant.available`, which is true for an oversellable out-of-stock variant,
 * and a variant change morphs the bar back to that server-rendered state.
 */
function syncStickyState() {
  const notice = mainNotice();
  if (!notice || notice.dataset.xtpReady === 'true') return;

  for (const button of document.querySelectorAll('sticky-add-to-cart ' + ADD_BUTTON)) {
    if (button instanceof HTMLButtonElement && !button.disabled) button.disabled = true;
  }
}

const sticky = document.querySelector('sticky-add-to-cart');
if (sticky) {
  syncStickyState();
  new MutationObserver(syncStickyState).observe(sticky, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['disabled'],
  });
}

/**
 * Confirmed preorder → checkout, not the cart drawer.
 *
 * Hooked on the cart event rather than on the click so the redirect only
 * happens once Shopify has actually accepted the line: a refused add resolves
 * with `didError`, and the shopper stays on the product page with the error
 * the form drew.
 */
document.addEventListener('shopify:cart:lines-update', (event) => {
  const e = /** @type {any} */ (event);
  if (e.action !== 'add') return;

  const form = event.target;
  if (!(form instanceof Element) || !form.closest('[data-xtp-preorder="true"]')) return;

  const notice = form.querySelector(NOTICE);
  if (!(notice instanceof HTMLElement) || !consented(notice)) return;

  e.promise
    ?.then(({ detail }) => {
      if (detail?.didError) return;
      window.location.assign(Shopify?.routes?.root ? Shopify.routes.root + 'checkout' : '/checkout');
    })
    .catch(() => {});
});
